import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { CustomLoading } from "components/CustomLoading";
import { MoreHorizontal, Trash2 } from "lucide-react";
import { User, loadUsers, displayName, initials, avatarColor } from "./user-display";
import QuestionForm from "./QuestionForm";
import ConfirmModal from "./ConfirmModal";
import { formatRelative } from "./category-rows";

interface Category {
    id: string;
    name: string;
    color: string | null;
    icon: string | null;
}

interface Question {
    id: string;
    authorId: string;
    title: string;
    body: string | null;
    categoryId: string | null;
    createdAt: string;
    updatedAt: string;
}

interface Reply {
    id: string;
    authorId: string;
    body: string;
    isAnswered: boolean;
    createdAt: string;
    parentCommentId: string | null;
}

type Tab = "asked" | "answered";

interface Props {
    categories: Category[];
    currentUserId: string | null;
    isAdmin: boolean;
    onSelectQuestion: (id: string) => void;
}

export default function QuestionsView({ categories, currentUserId, isAdmin, onSelectQuestion }: Props) {
    const [tab, setTab] = useState<Tab>("asked");
    const [loading, setLoading] = useState(true);
    const [asked, setAsked] = useState<Question[]>([]);
    const [answered, setAnswered] = useState<Question[]>([]);
    const [replyPreview, setReplyPreview] = useState<Record<string, { first: Reply | null; count: number }>>({});
    const [usersMap, setUsersMap] = useState<Map<string, User>>(new Map());
    const [askOpen, setAskOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<Question | null>(null);
    const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

    const categoriesById = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories]);
    const questions = tab === "asked" ? asked : answered;

    const load = () => {
        setLoading(true);
        Promise.all([
            axios.get("/api/manuals/questions?filter=asked"),
            axios.get("/api/manuals/questions?filter=answered"),
            loadUsers(),
        ])
            .then(([askedRes, answeredRes, users]) => {
                setUsersMap(new Map(users.map(u => [u.id, u])));
                const askedItems: Question[] = askedRes.data.success ? askedRes.data.items : [];
                const answeredItems: Question[] = answeredRes.data.success ? answeredRes.data.items : [];
                setAsked(askedItems);
                setAnswered(answeredItems);
                loadReplyPreviews([...askedItems, ...answeredItems].map(q => q.id));
            })
            .catch(() => toast.error("Failed to load questions"))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    useEffect(() => {
        if (!menuOpenId) return;
        const h = () => setMenuOpenId(null);
        document.addEventListener("click", h);
        return () => document.removeEventListener("click", h);
    }, [menuOpenId]);

    function loadReplyPreviews(ids: string[]) {
        Promise.all(
            ids.map(id =>
                axios.get(`/api/manuals/questions/${id}/comments`)
                    .then(r => ({ id, replies: r.data.success ? (r.data.items as Reply[]) : [] }))
                    .catch(() => ({ id, replies: [] as Reply[] }))
            )
        ).then(results => {
            const m: Record<string, { first: Reply | null; count: number }> = {};
            results.forEach(({ id, replies }) => {
                const topLevel = replies.filter(r => !r.parentCommentId);
                const count = replies.length;
                const answered = topLevel.find(r => r.isAnswered) || topLevel[0] || null;
                m[id] = { first: answered, count };
            });
            setReplyPreview(m);
        });
    }

    function handleDelete() {
        if (!deleteTarget) return;
        const id = deleteTarget.id;
        setDeleteTarget(null);
        axios.delete(`/api/manuals/questions/${id}`)
            .then(res => {
                if (res.data.success) {
                    toast.success("Question deleted");
                    setAsked(prev => prev.filter(q => q.id !== id));
                    setAnswered(prev => prev.filter(q => q.id !== id));
                } else {
                    toast.error(res.data.message || "Failed to delete");
                }
            })
            .catch(() => toast.error("Failed to delete"));
    }

    function authorView(authorId: string) {
        const u = usersMap.get(authorId);
        const name = u ? displayName(u) : authorId.slice(0, 8);
        return { name, color: avatarColor(authorId), initial: initials(name) };
    }

    return (
        <div className="m-cat-page">
            <div className="m-cat-header">
                <div>
                    <h1 className="m-cat-title">Questions &amp; Answers</h1>
                </div>
            </div>

            <div className="m-profile-tabs">
                <button
                    className={`m-cat-tab${tab === "asked" ? " active" : ""}`}
                    onClick={() => setTab("asked")}
                >
                    <span>Asked</span>
                    <span className="m-cat-tab-count">{asked.length}</span>
                </button>
                <button
                    className={`m-cat-tab${tab === "answered" ? " active" : ""}`}
                    onClick={() => setTab("answered")}
                >
                    <span>Answered</span>
                    <span className="m-cat-tab-count">{answered.length}</span>
                </button>
            </div>

            <div className="m-profile-body">
                {loading ? (
                    <div className="m-loading"><CustomLoading /></div>
                ) : questions.length === 0 ? (
                    <div className="m-questions-empty">
                        <span>{tab === "asked" ? "No unanswered questions" : "No answered questions yet"}</span>
                        <button className="m-btn m-btn-ghost m-btn-sm" onClick={() => setAskOpen(true)}>Ask one</button>
                    </div>
                ) : (
                    <div className="m-questions-list">
                        {questions.map(q => {
                            const a = authorView(q.authorId);
                            const cat = q.categoryId ? categoriesById.get(q.categoryId) : null;
                            const preview = replyPreview[q.id];
                            const canDelete = isAdmin || q.authorId === currentUserId;
                            return (
                                <div key={q.id} className="m-question-card">
                                    <div className="m-question-head">
                                        <span className="m-comment-avatar sm" style={{ background: a.color }}>{a.initial}</span>
                                        <span className="m-question-meta">
                                            <strong>{a.name}</strong> asked {formatRelative(q.createdAt)}
                                            {cat && <> in <span style={{ color: cat.color || undefined }}>{cat.name}</span></>}
                                        </span>
                                        {canDelete && (
                                            <div className="m-question-kebab-wrap">
                                                <button
                                                    className="m-cat-strip-kebab"
                                                    onClick={e => { e.stopPropagation(); setMenuOpenId(menuOpenId === q.id ? null : q.id); }}
                                                >
                                                    <MoreHorizontal size={14} />
                                                </button>
                                                {menuOpenId === q.id && (
                                                    <div className="m-cat-kebab-menu">
                                                        <button
                                                            className="m-cat-kebab-danger"
                                                            onClick={() => { setMenuOpenId(null); setDeleteTarget(q); }}
                                                        >
                                                            <Trash2 size={13} /> Delete
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <button className="m-question-title" onClick={() => onSelectQuestion(q.id)}>
                                        {q.title}
                                    </button>
                                    {preview?.first ? (
                                        <div className="m-question-reply-preview">
                                            <span className="m-comment-avatar sm" style={{ background: avatarColor(preview.first.authorId) }}>
                                                {initials(displayName(usersMap.get(preview.first.authorId) || { id: preview.first.authorId, name: "", email: null, role: null, isSystemAdministrator: false }))}
                                            </span>
                                            <span className="m-question-reply-body">{truncate(preview.first.body, 240)}</span>
                                        </div>
                                    ) : (
                                        <div className="m-question-reply-preview empty">
                                            <span className="m-question-reply-body">No replies yet.</span>
                                        </div>
                                    )}
                                    <div className="m-question-footer">
                                        <span>{preview?.count || 0} {(preview?.count || 0) === 1 ? "reply" : "replies"}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {askOpen && (
                <QuestionForm
                    categories={categories}
                    onSave={(created) => {
                        setAskOpen(false);
                        if (created) onSelectQuestion(created.id);
                        else load();
                    }}
                    onCancel={() => setAskOpen(false)}
                />
            )}

            {deleteTarget && (
                <ConfirmModal
                    title="Delete question"
                    message={`Delete "${deleteTarget.title}"? This cannot be undone.`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                />
            )}
        </div>
    );
}

function truncate(s: string, max: number): string {
    if (!s) return "";
    if (s.length <= max) return s;
    return s.slice(0, max).trimEnd() + "…";
}
